import { createContext, useContext } from 'react';
import type { RoomTimelineStore } from '../matrix/timeline';
import type {
  IncomingMatrixMessage,
  MediaSendProgressHandler,
  VoiceNote,
} from '../runtime/contracts';
import type { MatrixRtcPresence, MatrixRtcSessionHandle } from '../matrix/rtc';
import type { ChatMessage } from '../matrix/message';
import type { CallTerminalRecord } from '../call/callState';

export type CommsRuntimeStatus =
  | 'idle'
  | 'starting'
  | 'ready'
  | 'reconnecting'
  | 'failed'
  | 'stopping';

export interface DirectConversationSummary {
  operatorId: number;
  roomId: string;
  displayName?: string;
  latestMessage?: ChatMessage;
  unreadCount: number;
}

export interface CommsContextValue {
  status: CommsRuntimeStatus;
  matrixUserId: string | null;
  directRooms: ReadonlyMap<number, string>;
  unreadCounts: ReadonlyMap<number, number>;
  conversations: readonly DirectConversationSummary[];
  subscribeIncomingMessages(
    listener: (event: IncomingMatrixMessage) => void,
  ): () => void;
  ensureStarted(): Promise<void>;
  retryStart(): Promise<void>;
  stop(options?: { endSession?: boolean }): Promise<void>;
  ensureDirectRoom(operatorId: number): Promise<string>;
  getTimelineStore(roomId: string): RoomTimelineStore | null;
  selectRoom(roomId: string | null): void;
  markLatestVisible(roomId: string, eventId: string): void;
  clearLatestVisible(roomId: string, eventId: string): void;
  sendText(roomId: string, body: string): Promise<void>;
  retryMessage(roomId: string, eventId: string): Promise<void>;
  sendImage(
    roomId: string,
    file: File,
    onProgress?: MediaSendProgressHandler,
  ): Promise<void>;
  sendVoice(
    roomId: string,
    voiceNote: VoiceNote,
    onProgress?: MediaSendProgressHandler,
  ): Promise<void>;
  fetchMediaBlob(mxc: string): Promise<Blob>;
  watchRoomCall(
    roomId: string,
    listener: (presence: MatrixRtcPresence) => void,
  ): () => void;
  joinRtcCall(
    roomId: string,
    liveKitServiceUrl: string,
  ): MatrixRtcSessionHandle;
  sendCallTerminal(roomId: string, record: CallTerminalRecord): Promise<string>;
}

export const CommsContext = createContext<CommsContextValue | null>(null);

export const useComms = () => {
  const value = useContext(CommsContext);
  if (!value) throw new Error('CommsProvider is required.');
  return value;
};
